'use client';

import { useEffect, useState } from 'react';

import type { TimelineRow } from '@/components/assistant-ui/thread/trail-records';
import { STATUS_TRANSITION_MS } from '@/components/assistant-ui/thread/trail-step-icon';

/** The step before it fades its glyph out and its check in, one transition each. */
export const CLOSING_ROW_HOLD_MS = 2 * STATUS_TRANSITION_MS;
/** How long the closing row shows the thinking dot before its check fades in. */
export const CLOSING_ROW_DOT_MS = STATUS_TRANSITION_MS;

type Stage = 'held' | 'dot' | 'shown';

/**
 * The rows a trail shows while the rows that arrive after its run stopped (the closing row) make
 * their entrance. They arrive already completed, in the same moment the step before them turns
 * completed, so both checks animated at once. They wait for that step's check, slide in in progress
 * (`dotRowIds`: on the thinking dot), then turn completed with the same crossfade every step gets.
 */
export function useClosingRowEntrance({
  rows,
  liveRowCount,
}: {
  rows: readonly TimelineRow[];
  /** How many rows the trail had while it was last live; undefined for a trail never seen live. */
  liveRowCount: number | undefined;
}): { shownRows: readonly TimelineRow[]; entering: boolean; dotRowIds: readonly string[] } {
  const lateCount = liveRowCount === undefined ? 0 : Math.max(0, rows.length - liveRowCount);
  const lateKey = lateCount > 0 ? rows[rows.length - 1].id : undefined;
  const [entrance, setEntrance] = useState<{ key?: string; stage: Stage }>({ stage: 'shown' });
  if (entrance.key !== lateKey) setEntrance({ key: lateKey, stage: lateKey ? 'held' : 'shown' });

  useEffect(() => {
    if (entrance.stage === 'shown') return;
    const id = setTimeout(
      () => setEntrance(({ key }) => ({ key, stage: entrance.stage === 'held' ? 'dot' : 'shown' })),
      entrance.stage === 'held' ? CLOSING_ROW_HOLD_MS : CLOSING_ROW_DOT_MS,
    );
    return () => clearTimeout(id);
  }, [entrance]);

  const stage = entrance.key === lateKey ? entrance.stage : lateKey ? 'held' : 'shown';
  if (stage === 'shown') return { shownRows: rows, entering: false, dotRowIds: [] };
  const liveRows = rows.slice(0, rows.length - lateCount);
  if (stage === 'held') return { shownRows: liveRows, entering: true, dotRowIds: [] };
  const lateRows = rows.slice(-lateCount);
  return {
    shownRows: [...liveRows, ...lateRows.map(row => ({ ...row, status: 'in-progress' as const }))],
    entering: true,
    dotRowIds: lateRows.map(row => row.id),
  };
}
